import { buildStageMetrics } from "../reports/crmReportMetrics";
import { excluirClientesArchivados } from "../archiveUtils";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { obtenerFechaHoyISO, toLocalISOString } from "../../../utils/horaLocal";
import { DEFAULT_STAGES, normalizarPipeline, ETAPA_VENTA_CERRADA, funnelAcumulativo } from "../pipelineUtils";
import { normalizarCanalNombre } from "../canalUtils";
import { crearEntradaHistorialEdicion } from "../leadHistorialUtils";
import { totalItems } from "../ventaUtils";
import { coreBaseApi as api, createCrmQueue, diffCrmStates, hasCrmChanges, crmStorageKey } from "../services/coreBaseApi";
import { resumenComercialGlobal } from "../expansion/expansionMetrics";
import { upsertSnapshotForToday, isEtapaExpansionAbierta } from "../expansion/pipelineSnapshotUtils";

import { connectCrmSocket, createCrmRefreshController, shareCrmState } from "../services/crmRealtime";

const STORAGE_KEY = "corebase_crm_v1";
const BACKUP_KEY = "corebase_crm_v1_backup";
const SYNC_META_KEY = "corebase_crm_v1_sync_meta";

const uid = () => window.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
const hoy = () => obtenerFechaHoyISO();
const ahora = () => toLocalISOString();

const INITIAL_STATE = {
  config: {
    nombreNegocio: "Mi Negocio",
    moneda: "BOB",
    codigoPais: "591",
    codigosPaisExtra: [],  // ["1", "54", "57", ...]
    etapasPipeline: DEFAULT_STAGES,
    canales: ["whatsapp", "presencial", "redes_sociales", "telefono", "referido", "otro"],
    productos: [],       // { id, nombre, precio, esRecurrente?, diasVigencia?, diasRecordatorio? }
    vendedores: [],      // { id, nombre, activo }
    metodosPago: ["efectivo", "transferencia"],
    plantillasWA: [],  // { id, nombre, mensaje, etapa?, favorita? } — variables: {nombre}, {curso}, {negocio}, {precio}
    zonas: [],              // ["Norte", "Sur", "Centro", ...] — zonas geográficas
    ubicacionNegocio: null, // { lat, lng } — centro por defecto del mapa
    diasEntreSeguimiento: 7, // días para auto-agendar revisita (0 = desactivado)
    importacionLeads: { totalImportados: 0, totalDuplicados: 0, eventos: [] },
    alertasLeidas: [], // ids de alertas vigentes marcadas como leídas (siguen en Acciones pendientes)
  },
  leads: [],
  contactosHistorial: [],
  tareasFollowup: [],
  ventas: [],
  pagosCredito: [],     // { id, ventaId, clienteKey, monto, metodo, nota, fecha }
  resumenDiario: [],
  historialEtapas: [],  // { id, leadId, etapaAnterior, etapaNueva, fechaHora }
  historialEdicionesLead: [], // { id, leadId, fechaHora, cambios: [{ campo, etiqueta, valorAnterior, valorNuevo }] }
  historialAsignaciones: [],
  oportunidadesExpansion: [],
  snapshotsPipeline: [],
  _lastModified: null,  // ISO timestamp — se actualiza con cada cambio
};

const withPipelineSnapshot = (prev) => ({
  ...prev,
  snapshotsPipeline: upsertSnapshotForToday(prev, hoy(), ahora()),
});

function normalizarEstado(estado) {
  return { ...estado, config: normalizarPipeline(estado.config, estado.leads, estado.historialEtapas) };
}

function cargarEstado(workspaceId) {
  try {
    const raw = localStorage.getItem(crmStorageKey(STORAGE_KEY, workspaceId));
    if (!raw) return normalizarEstado(INITIAL_STATE);
    const parsed = JSON.parse(raw);
    const estado = {
      ...INITIAL_STATE,
      ...parsed,
      config: { ...INITIAL_STATE.config, ...parsed.config },
      historialEdicionesLead: parsed.historialEdicionesLead || [],
      oportunidadesExpansion: parsed.oportunidadesExpansion || [],
      snapshotsPipeline: parsed.snapshotsPipeline || [],
    };

    // Migración: crear historial inicial para leads que no tienen entrada
    if (estado.leads.length > 0) {
      const leadsConHistorial = new Set((estado.historialEtapas || []).map(h => h.leadId));
      const faltantes = estado.leads.filter(l => !leadsConHistorial.has(l.id));
      if (faltantes.length > 0) {
        estado.historialEtapas = [
          ...(estado.historialEtapas || []),
          ...faltantes.map(l => ({
            id: uid(),
            leadId: l.id,
            etapaAnterior: null,
            etapaNueva: l.etapa,
            fechaHora: l.creadoEn || ahora(),
          })),
        ];
      }
    }

    return normalizarEstado(estado);
  } catch {
    return normalizarEstado(INITIAL_STATE);
  }
}

// ── SYNC HELPERS ────────────────────────────────────
function guardarSyncMeta(meta, workspaceId) {
  try {
    localStorage.setItem(crmStorageKey(SYNC_META_KEY, workspaceId), JSON.stringify(meta));
  } catch { /* ignore */ }
}

function cargarSyncMeta(workspaceId) {
  try {
    const raw = localStorage.getItem(crmStorageKey(SYNC_META_KEY, workspaceId));
    return raw ? JSON.parse(raw) : { ultimoSync: null, pendiente: false };
  } catch {
    return { ultimoSync: null, pendiente: false };
  }
}

function hacerBackupLocal(estado, workspaceId) {
  try {
    localStorage.setItem(crmStorageKey(BACKUP_KEY, workspaceId), JSON.stringify(estado));
  } catch { /* ignore — quota exceeded fallback */ }
}

// ── LEAD SCORING ────────────────────────────────────
function calcularScore(lead, contactos, tareas) {
  let score = 0;
  // Recency: último contacto reciente = más puntos
  const contactosLead = contactos.filter(c => c.leadId === lead.id);
  if (contactosLead.length > 0) {
    const ultimo = contactosLead.sort((a, b) => b.fecha.localeCompare(a.fecha))[0];
    const dias = Math.floor((Date.now() - new Date(ultimo.fecha).getTime()) / 86400000);
    if (dias <= 1) score += 30;
    else if (dias <= 3) score += 20;
    else if (dias <= 7) score += 10;
  }
  // Engagement: más contactos = más interés
  score += Math.min(contactosLead.length * 5, 25);
  // Pipeline stage
  const etapaScores = { nuevo: 5, contactado: 15, interesado: 30, inscrito: 0, perdido: 0 };
  score += etapaScores[lead.etapa] || 0;
  // Tiene tarea pendiente (activo)
  const tieneTarea = tareas.some(t => t.leadId === lead.id && !t.completada && !t.cancelada);
  if (tieneTarea) score += 10;
  // Canal bonus (WhatsApp suele convertir bien)
  if (lead.canal === "whatsapp") score += 5;
  if (lead.canal === "presencial") score += 10;

  return Math.min(score, 100);
}

export function useCoreBaseStore(workspace = { isOwner: true }) {
  const isOwner = workspace.isOwner;
  const workspaceId = workspace.id;
  const coreBaseQueue = useMemo(() => createCrmQueue(workspaceId), [workspaceId]);
  const coreBaseApi = useMemo(() => ({ ...api, sync: (legacy, revision) => api.sync(legacy, workspaceId, revision), saveSeller: seller => api.saveSeller(seller, workspaceId), createEntry: changes => api.createEntry(changes, workspaceId) }), [workspaceId]);
  const storageScope = useMemo(() => crmStorageKey('', workspaceId), [workspaceId]);
  const cacheKey = useCallback(key => `${key}${storageScope}`, [storageScope]);
  const [state, setState] = useState(() => isOwner ? cargarEstado(workspaceId) : normalizarEstado(INITIAL_STATE));
  const [ready, setReady] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [accessContext, setAccessContext] = useState(workspace);
  const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | ok | error
  const [ultimoSync, setUltimoSync] = useState(() => cargarSyncMeta(workspaceId).ultimoSync);
  const [cambiosPendientes, setCambiosPendientes] = useState(() => coreBaseQueue.list().length > 0);
  const mountedRef = useRef(true);
  const hydratedRef = useRef(false);
  const revisionRef = useRef(null);
  const flushingRef = useRef(false);
  const flushPromiseRef = useRef(null);
  const pullPromiseRef = useRef(null);
  const refreshControllerRef = useRef(null);
  const pullRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState(navigator.onLine === false ? "offline" : "connecting");
  const [syncConflict, setSyncConflict] = useState(null);
  const conflictRef = useRef(null);
  conflictRef.current = syncConflict;
  const stateRef = useRef(state);
  const initialStateRef = useRef(state);
  const configBaselineRef = useRef(state.config);
  const lastQueuedStateRef = useRef(state);

  stateRef.current = state;

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const flushTransactions = useCallback(() => {
    if (flushPromiseRef.current) return flushPromiseRef.current;
    if (!localStorage.getItem("token") || conflictRef.current) return Promise.resolve({ ok: false, pending: coreBaseQueue.list().length });
    if (!coreBaseQueue.list().length) return Promise.resolve({ ok: true, pending: 0 });
    flushingRef.current = true;
    if (mountedRef.current) setSyncStatus("syncing");
    const operation = async () => {
      let result;
      try {
        result = await coreBaseQueue.flush(async entry => {
          const saved = await coreBaseApi.transact(entry);
          if (mountedRef.current && Number.isInteger(saved.revision)) refreshControllerRef.current?.notify(saved.revision);
        });
        if (mountedRef.current) {
          setCambiosPendientes(result.pending > 0);
          setSyncStatus(result.ok ? "ok" : "error");
          if (result.ok) {
            const timestamp = ahora(); setUltimoSync(timestamp);
            guardarSyncMeta({ ultimoSync: timestamp, pendiente: false }, workspaceId);
          } else if (result.error?.response?.data?.code === "CRM_CONFLICT") {
            const detail = result.error.response.data;
            conflictRef.current = detail; setSyncConflict(detail);
          } else if ([401, 403].includes(result.error?.response?.status)) {
            // Reassignment can reject one write without revoking the workspace.
            try {
              await coreBaseApi.sync(null);
              if (mountedRef.current) {
                const detail = { code: "CRM_CONFLICT", message: "Los permisos de un registro cambiaron. Tus cambios pendientes se conservan para revisarlos." };
                conflictRef.current = detail; setSyncConflict(detail);
              }
            } catch (error) {
              if (mountedRef.current && [401, 403].includes(error.response?.status)) {
                setAccessError("Tu acceso a este espacio ha terminado");
                localStorage.removeItem(cacheKey(STORAGE_KEY));
                localStorage.removeItem(cacheKey(BACKUP_KEY));
              }
            }
          }
        }
        return result;
      } finally {
        flushingRef.current = false; flushPromiseRef.current = null;
        if (mountedRef.current) {
          refreshControllerRef.current?.resume();
          if (result?.pending && !result.error) setTimeout(() => { if (mountedRef.current) flushTransactions(); }, 0);
        }
      }
    };
    flushPromiseRef.current = operation();
    return flushPromiseRef.current;
  }, [coreBaseApi, coreBaseQueue, cacheKey, workspaceId]);
  // The first authenticated load performs the one-time migration. The server
  // merges the newest browser and cloud blobs before exposing collections.
  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    let cancelled = false;
    const initialize = async () => {
      setSyncStatus("syncing");
      const sentLegacy = initialStateRef.current;
      try {
        let response = await coreBaseApi.sync(sentLegacy);
        if (cancelled || !mountedRef.current) return;
        // Compare against the local starting point, never against an older remote snapshot.
        const localChanges = diffCrmStates(sentLegacy, stateRef.current, isOwner);
        if (hasCrmChanges(localChanges)) coreBaseQueue.enqueue(coreBaseApi.createEntry(localChanges));
        lastQueuedStateRef.current = stateRef.current;
        // A revision is applied only when its snapshot is installed locally.
        const beforeFlush = stateRef.current;
        const hadPending = coreBaseQueue.list().length > 0;
        const flushed = await flushTransactions();
        if (flushed.ok && hadPending) response = await coreBaseApi.sync(null);
        if (cancelled || !mountedRef.current) return;
        hydratedRef.current = true;
        setAccessContext(response.workspace);
        setReady(true);
        if (!flushed.ok || coreBaseQueue.list().length || stateRef.current !== beforeFlush) {
          const duringFlush = diffCrmStates(lastQueuedStateRef.current, stateRef.current, isOwner);
          if (hasCrmChanges(duringFlush)) coreBaseQueue.enqueue(coreBaseApi.createEntry(duringFlush));
          lastQueuedStateRef.current = stateRef.current;
          setCambiosPendientes(coreBaseQueue.list().length > 0);
          refreshControllerRef.current?.wake();
          return;
        }
        const remote = shareCrmState(stateRef.current, normalizarEstado({ ...INITIAL_STATE, ...response.state, config: { ...INITIAL_STATE.config, ...response.state?.config } }));
        revisionRef.current = response.revision;
        stateRef.current = remote;
        configBaselineRef.current = response.state?.config || {};
        lastQueuedStateRef.current = remote;
        setState(remote);
        setSyncStatus("ok");
      } catch (error) {
        if (!cancelled) setAccessError(error.response?.data?.message || 'No se pudo verificar el acceso al espacio');
        console.error("CoreBase transactional sync error:", error);
        hydratedRef.current = false;
        lastQueuedStateRef.current = stateRef.current;
        if (!cancelled && mountedRef.current) setSyncStatus("error");
      }
    };
    initialize();
    const onOnline = () => flushTransactions();
    window.addEventListener("online", onOnline);
    return () => { cancelled = true; window.removeEventListener("online", onOnline); };
  }, [flushTransactions, coreBaseApi, coreBaseQueue, isOwner]);

  // Persistir en localStorage + marcar _lastModified + backup
  useEffect(() => {
    const conTimestamp = { ...state, _lastModified: ahora() };
    localStorage.setItem(cacheKey(STORAGE_KEY), JSON.stringify(conTimestamp));
    // Backup de seguridad cada vez que cambia
    hacerBackupLocal(conTimestamp, workspaceId);
  }, [state, cacheKey, workspaceId]);

  // Cada cambio se convierte en un delta idempotente y se conserva en FIFO
  // hasta que el servidor confirma la transacción.
  useEffect(() => {
    if (!hydratedRef.current || !localStorage.getItem("token")) return;
    const changes = diffCrmStates(lastQueuedStateRef.current, state, isOwner);
    if (!hasCrmChanges(changes)) { lastQueuedStateRef.current = state; refreshControllerRef.current?.resume(); return; }
    if (changes.config) { changes.configBase = configBaselineRef.current; configBaselineRef.current = changes.config; }
    coreBaseQueue.enqueue(coreBaseApi.createEntry(changes));
    lastQueuedStateRef.current = state;
    setCambiosPendientes(true);
    flushTransactions();
  }, [state, flushTransactions, coreBaseApi, coreBaseQueue, isOwner]);

  useEffect(() => {
    if (!hydratedRef.current || !isOwner) return;
    setState((prev) => {
      const nextSnaps = upsertSnapshotForToday(prev, hoy(), ahora());
      const old = prev.snapshotsPipeline || [];
      if (JSON.stringify(old) === JSON.stringify(nextSnaps)) return prev;
      return { ...prev, snapshotsPipeline: nextSnaps };
    });
  }, [isOwner, state.leads, state.oportunidadesExpansion, state.ventas, state.config?.diasEntreSeguimiento]);

  // Forzar el vaciado de la cola transaccional (botón de sync).
  const forzarSync = useCallback(() => flushTransactions(), [flushTransactions]);

  // Background refresh is silent and single-flight. It never replaces local
  // edits that appeared while the server snapshot was being read.
  const pullSeguro = useCallback(({ background = false } = {}) => {
    if (pullPromiseRef.current) return pullPromiseRef.current;
    const operation = async () => {
      if (!localStorage.getItem("token")) return { ok: false, razon: "No autenticado" };
      if (conflictRef.current) return { ok: false, razon: "Revisa los cambios pendientes" };
      try {
        const flushed = await flushTransactions();
        if (!flushed.ok) return { ok: false, razon: "Hay cambios pendientes sin confirmar" };
        if (stateRef.current !== lastQueuedStateRef.current) return { ok: false, razon: "Hay cambios nuevos pendientes" };
        const localBeforeRead = stateRef.current;
        const response = await coreBaseApi.sync(null, revisionRef.current);
        if (!mountedRef.current) return { ok: false, razon: "Espacio cerrado" };
        if (stateRef.current !== localBeforeRead || coreBaseQueue.list().length) return { ok: false, razon: "Hay cambios nuevos pendientes" };
        if (Number.isInteger(revisionRef.current) && response.revision < revisionRef.current) return { ok: false, razon: "Respuesta anterior ignorada" };
        revisionRef.current = response.revision;
        if (!response.notModified) {
          const remote = normalizarEstado({ ...INITIAL_STATE, ...response.state, config: { ...INITIAL_STATE.config, ...response.state?.config } });
          const shared = shareCrmState(localBeforeRead, remote);
          configBaselineRef.current = response.state?.config || {};
          lastQueuedStateRef.current = shared; stateRef.current = shared;
          setState(shared);
          setAccessContext(previous => JSON.stringify(previous) === JSON.stringify(response.workspace) ? previous : response.workspace);
        }
        if (!background) { setSyncStatus("ok"); setUltimoSync(ahora()); }
        return { ok: true, razon: response.notModified ? "Sin cambios nuevos" : "Datos actualizados" };
      } catch (error) {
        if (mountedRef.current) {
          if ([401, 403].includes(error.response?.status)) {
            setAccessError("Tu acceso a este espacio ha terminado");
            localStorage.removeItem(cacheKey(STORAGE_KEY)); localStorage.removeItem(cacheKey(BACKUP_KEY));
          } else if (!background) setSyncStatus("error");
        }
        return { ok: false, razon: "No se pudo actualizar; tus cambios locales se conservan" };
      } finally { pullPromiseRef.current = null; }
    };
    pullPromiseRef.current = operation().finally(() => { pullPromiseRef.current = null; });
    return pullPromiseRef.current;
  }, [flushTransactions, coreBaseApi, coreBaseQueue, cacheKey]);
  pullRef.current = pullSeguro;

  useEffect(() => {
    if (!ready || !workspaceId || !localStorage.getItem("token") || accessError) return;
    let closed = false, socketConnected = false;
    const controller = createCrmRefreshController({
      refresh: () => pullRef.current({ background: true }),
      getRevision: () => revisionRef.current,
      canRefresh: () => hydratedRef.current && !document.hidden && navigator.onLine !== false && !conflictRef.current && !flushingRef.current,
    });
    refreshControllerRef.current = controller;
    const disconnect = connectCrmSocket({
      workspaceId,
      onChange: revision => controller.notify(revision),
      onConnect: () => { socketConnected = true; if (!closed) { setConnectionStatus("connected"); controller.wake(); } },
      onDisconnect: () => { socketConnected = false; if (!closed) setConnectionStatus(navigator.onLine === false ? "offline" : "reconnecting"); },
      onUnavailable: () => { socketConnected = false; if (!closed) { setConnectionStatus("polling"); } },
      onDenied: () => { if (!closed) { setAccessError("Tu acceso al espacio CRM debe verificarse de nuevo. Vuelve a iniciar sesión."); localStorage.removeItem(cacheKey(STORAGE_KEY)); localStorage.removeItem(cacheKey(BACKUP_KEY)); } },
    });
    const onOnline = () => { setConnectionStatus(socketConnected ? "connected" : "reconnecting"); controller.wake(); };
    const onOffline = () => setConnectionStatus("offline");
    const onVisible = () => { if (!document.hidden) controller.wake(); };
    const onStorage = event => { if (event.key?.startsWith(cacheKey("corebase_crm_tx_v1") + ":")) controller.wake(); };
    window.addEventListener("online", onOnline); window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible); window.addEventListener("storage", onStorage);
    // Missed notifications and server restarts recover without requiring user action.
    const timer = setInterval(() => controller.wake(), 30000);
    controller.wake();
    return () => {
      closed = true; clearInterval(timer); controller.dispose(); disconnect();
      refreshControllerRef.current = null;
      window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("storage", onStorage);
    };
  }, [ready, workspaceId, accessError, cacheKey]);

  const exportPendingChanges = useCallback(() => {
    const blob = new Blob([JSON.stringify({ workspaceId, pending: coreBaseQueue.list(), localState: stateRef.current }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const anchor = document.createElement("a");
    anchor.href = url; anchor.download = "crm-cambios-pendientes.json"; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [workspaceId, coreBaseQueue]);

  const useServerVersion = useCallback(async () => {
    // Preserve the complete draft before the user explicitly chooses server data.
    try {
      const entries = coreBaseQueue.list();
      localStorage.setItem(cacheKey("corebase_crm_conflict_backup"), JSON.stringify({ savedAt: ahora(), entries, state: stateRef.current }));
      const before = stateRef.current;
      const response = await coreBaseApi.sync(null);
      if (!mountedRef.current || before !== stateRef.current || JSON.stringify(entries) !== JSON.stringify(coreBaseQueue.list())) return;
      const remote = normalizarEstado({ ...INITIAL_STATE, ...response.state, config: { ...INITIAL_STATE.config, ...response.state?.config } });
      coreBaseQueue.remove(entries.map(entry => entry.transactionId)); setAccessContext(response.workspace); conflictRef.current = null; setSyncConflict(null);
      configBaselineRef.current = response.state?.config || {};
      revisionRef.current = response.revision; stateRef.current = remote; lastQueuedStateRef.current = remote;
      setState(remote); setCambiosPendientes(false); setSyncStatus("ok");
      refreshControllerRef.current?.resume();
    } catch { setSyncStatus("error"); }
  }, [coreBaseQueue, coreBaseApi, cacheKey]);
  // Restaurar desde backup local (emergencia)
  const restaurarDesdeBackup = useCallback(() => {
    if (!isOwner) return false;
    try {
      const backup = localStorage.getItem(cacheKey(BACKUP_KEY));
      if (!backup) return false;
      const parsed = JSON.parse(backup);
      setState(normalizarEstado({ ...INITIAL_STATE, ...parsed, config: { ...INITIAL_STATE.config, ...parsed.config } }));
      return true;
    } catch {
      return false;
    }
  }, [cacheKey, isOwner]);

  // ── CONFIG ──────────────────────────────────────
  const actualizarConfig = useCallback((cambios) => {
    setState(prev => ({
      ...prev,
      config: { ...prev.config, ...cambios },
    }));
  }, []);

  const agregarCanalesSiFaltan = useCallback((canales) => {
    setState(prev => {
      const actuales = prev.config.canales || [];
      const vistos = new Set(actuales.map((c) => c.toLowerCase()));
      const nuevos = [];
      (canales || []).forEach((raw) => {
        const canal = normalizarCanalNombre(raw);
        if (!canal || vistos.has(canal)) return;
        vistos.add(canal);
        nuevos.push(canal);
      });
      if (nuevos.length === 0) return prev;
      return {
        ...prev,
        config: { ...prev.config, canales: [...actuales, ...nuevos] },
      };
    });
  }, []);

  // ── PRODUCTOS (catálogo) ────────────────────────
  const guardarEtapas = useCallback((catalogo, destinos = {}) => {
    setState(prev => {
      const ids = new Set(catalogo.map(e => e.id));
      if (ids.size !== catalogo.length || catalogo.some(e => !e.nombre.trim()) ||
          prev.config.etapasCatalogo.some(e => !ids.has(e.id))) return prev;
      const activas = catalogo.filter(e => e.activa).map(e => e.id);
      if (!activas.length) return prev;
      const ocultadas = prev.config.etapasPipeline.filter(id => !activas.includes(id));
      if (ocultadas.some(id => prev.leads.some(l => l.etapa === id) && !activas.includes(destinos[id]))) return prev;
      const historial = [...prev.historialEtapas];
      const leads = prev.leads.map(l => {
        if (!ocultadas.includes(l.etapa)) return l;
        historial.push({ id: uid(), leadId: l.id, etapaAnterior: l.etapa, etapaNueva: destinos[l.etapa], fechaHora: ahora() });
        return { ...l, etapa: destinos[l.etapa] };
      });
      return { ...prev, leads, historialEtapas: historial, config: { ...prev.config, etapasCatalogo: catalogo, etapasPipeline: activas } };
    });
  }, []);

  const agregarProducto = useCallback((producto) => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        productos: [...prev.config.productos, { id: uid(), ...producto }],
      },
    }));
  }, []);

  const agregarProductosMasivo = useCallback((lista = []) => {
    const existentes = new Set(
      (stateRef.current.config.productos || []).map(p => String(p.nombre || "").trim().toLowerCase()).filter(Boolean)
    );
    const added = [];
    let omitidos = 0;
    lista.forEach((item) => {
      const nombre = String(item?.nombre || "").trim();
      if (!nombre) { omitidos += 1; return; }
      const key = nombre.toLowerCase();
      if (existentes.has(key)) { omitidos += 1; return; }
      existentes.add(key);
      const prod = { id: uid(), nombre, precio: parseFloat(item.precio) || 0 };
      if (item.esRecurrente) {
        prod.esRecurrente = true;
        prod.diasVigencia = parseInt(item.diasVigencia, 10) || 30;
        prod.diasRecordatorio = parseInt(item.diasRecordatorio, 10) || 7;
      }
      added.push(prod);
    });
    if (added.length) {
      setState(prev => ({
        ...prev,
        config: { ...prev.config, productos: [...prev.config.productos, ...added] },
      }));
    }
    return { importados: added.length, omitidos };
  }, []);

  const editarProducto = useCallback((id, cambios) => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        productos: prev.config.productos.map(p => p.id === id ? { ...p, ...cambios } : p),
      },
    }));
  }, []);

  // ── VENDEDORES ──────────────────────────────────
  const guardarVendedor = useCallback(async (seller) => {
    const flushed = await flushTransactions();
    if (!flushed.ok) throw new Error('Primero sincroniza los cambios pendientes');
    const response = await coreBaseApi.saveSeller(seller);
    // Refresh canonically; never replace other config edits made while this request ran.
    refreshControllerRef.current?.notify(response.revision);
    return response;
  }, [flushTransactions, coreBaseApi]);
  const agregarVendedor = (nombre, email, id, expectedSeller) => guardarVendedor({ nombre, email, id, expectedSeller: id ? expectedSeller || stateRef.current.config.vendedores.find(v => v.id === id) : undefined, activo: id ? stateRef.current.config.vendedores.find(v => v.id === id)?.activo !== false : true });
  const toggleVendedor = (id) => {
    const seller = stateRef.current.config.vendedores.find(v => v.id === id);
    return guardarVendedor({ ...seller, expectedSeller: seller, activo: !seller.activo });
  };

  // ── PLANTILLAS WHATSAPP ─────────────────────────
  const agregarPlantilla = useCallback((plantilla) => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        plantillasWA: [...prev.config.plantillasWA, { id: uid(), ...plantilla }],
      },
    }));
  }, []);

  const editarPlantilla = useCallback((id, cambios) => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        plantillasWA: prev.config.plantillasWA.map(p => p.id === id ? { ...p, ...cambios } : p),
      },
    }));
  }, []);

  const eliminarPlantilla = useCallback((id) => {
    setState(prev => ({
      ...prev,
      config: {
        ...prev.config,
        plantillasWA: prev.config.plantillasWA.filter(p => p.id !== id),
      },
    }));
  }, []);

  // ── LEADS CRUD ──────────────────────────────────
  const agregarLead = useCallback((lead) => {
    const nuevoLead = {
      id: uid(),
      fechaContacto: hoy(),
      nombre: "",
      edad: "",
      telefono: "",
      cursoInteres: "",
      montoEstimado: null,
      canal: "whatsapp",
      etapa: "nuevo",
      ultimoContacto: null,
      proximoSeguimiento: null,
      observaciones: "",
      vendedorId: null,
      zona: "",
      location: null,  // { lat, lng }
      creadoEn: ahora(),
      ...lead,
    };
    setState(prev => ({
      ...prev,
      leads: [...prev.leads, nuevoLead],
      historialEtapas: [...prev.historialEtapas, {
        id: uid(),
        leadId: nuevoLead.id,
        etapaAnterior: null,
        etapaNueva: nuevoLead.etapa,
        fechaHora: nuevoLead.creadoEn,
      }],
    }));
    return nuevoLead.id;
  }, []);

  const editarLead = useCallback((id, cambios) => {
    setState(prev => {
      const anterior = prev.leads.find(l => l.id === id);
      if (!anterior) return prev;
      const etapa = cambios.etapa ?? anterior.etapa;
      const entradaEdicion = crearEntradaHistorialEdicion(
        id,
        anterior,
        cambios,
        prev.config,
        uid,
        ahora
      );
      const historialEdicionesLead = entradaEdicion
        ? [...(prev.historialEdicionesLead || []), entradaEdicion]
        : prev.historialEdicionesLead || [];
      return {
        ...prev,
        leads: prev.leads.map(l => l.id === id ? { ...l, ...cambios, id, creadoEn: l.creadoEn, etapa } : l),
        historialEtapas: etapa === anterior.etapa ? prev.historialEtapas : [...prev.historialEtapas, {
          id: uid(), leadId: id, etapaAnterior: anterior.etapa, etapaNueva: etapa, fechaHora: ahora(),
        }],
        historialEdicionesLead,
      };
    });
  }, []);

  const archivarLead = useCallback((id, archivado = true) => {
    if (!isOwner) return;
    editarLead(id, { archivado });
  }, [isOwner, editarLead]);

  const eliminarLead = useCallback((id) => {
    setState(prev => ({
      ...prev,
      leads: prev.leads.filter(l => l.id !== id),
    }));
  }, []);

  const moverLeadEtapa = useCallback((id, nuevaEtapa) => {
    setState(prev => {
      const lead = prev.leads.find(l => l.id === id);
      if (!lead || lead.etapa === nuevaEtapa) return prev;
      return {
        ...prev,
        leads: prev.leads.map(l => l.id === id ? { ...l, etapa: nuevaEtapa } : l),
        historialEtapas: [...prev.historialEtapas, {
          id: uid(),
          leadId: id,
          etapaAnterior: lead.etapa,
          etapaNueva: nuevaEtapa,
          fechaHora: ahora(),
        }],
      };
    });
  }, []);

  const importarLeadsMasivo = useCallback(({ nuevos, duplicados, proximoSeguimiento, actualizarDuplicados = false }) => {
    const ts = ahora();
    setState((prev) => {
      const leads = [...prev.leads];
      const historialEtapas = [...prev.historialEtapas];
      const contactosHistorial = [...prev.contactosHistorial];

      nuevos.forEach((lead) => {
        const nuevoLead = {
          fechaContacto: hoy(),
          nombre: "",
          edad: "",
          telefono: "",
          cursoInteres: "",
          montoEstimado: null,
          canal: "whatsapp",
          etapa: "nuevo",
          ultimoContacto: null,
          proximoSeguimiento: null,
          observaciones: "",
          vendedorId: null,
          zona: "",
          location: null,
          creadoEn: ts,
          ...lead,
          id: lead.id || uid(),
        };
        leads.push(nuevoLead);
        historialEtapas.push({
          id: uid(),
          leadId: nuevoLead.id,
          etapaAnterior: null,
          etapaNueva: nuevoLead.etapa,
          fechaHora: nuevoLead.creadoEn,
        });
      });

      const leadIndices = new Map(leads.map((lead, index) => [lead.id, index]));
      const historialEdicionesLead = [...(prev.historialEdicionesLead || [])];
      duplicados.forEach(({ leadId, lead, numeroFila, camposImportados = [] }) => {
        const targetIndex = leadIndices.get(leadId);
        if (targetIndex === undefined || leads[targetIndex].archivado) return;
        if (actualizarDuplicados) {
          const anterior = leads[targetIndex];
          const cambios = Object.fromEntries(camposImportados.filter(key => key in lead).map(key => [key, lead[key]]));
          const entrada = crearEntradaHistorialEdicion(leadId, anterior, cambios, prev.config, uid, ahora);
          if (entrada) historialEdicionesLead.push(entrada);
          if (cambios.etapa && cambios.etapa !== anterior.etapa) historialEtapas.push({ id: uid(), leadId, etapaAnterior: anterior.etapa, etapaNueva: cambios.etapa, fechaHora: ts });
          leads[targetIndex] = { ...anterior, ...cambios };
        }
        const nota = `Importación masiva: lead repetido (fila ${numeroFila}, mismo nombre y teléfono).${
          lead.cursoInteres ? ` Interés en archivo: ${lead.cursoInteres}.` : ""
        }`;
        contactosHistorial.push({
          id: uid(),
          leadId,
          fecha: ts,
          tipo: "otro",
          nota,
        });
        const idx = leadIndices.get(leadId);
        if (idx !== undefined) {
          leads[idx] = {
            ...leads[idx],
            ultimoContacto: ts,
            proximoSeguimiento: actualizarDuplicados ? leads[idx].proximoSeguimiento : proximoSeguimiento || leads[idx].proximoSeguimiento,
          };
        }
      });

      const prevStats = prev.config.importacionLeads || { totalImportados: 0, totalDuplicados: 0, eventos: [] };
      const evento = {
        id: uid(),
        fecha: ts,
        importados: nuevos.length,
        duplicados: duplicados.length,
      };
      const eventos = [evento, ...(prevStats.eventos || [])].slice(0, 30);

      return {
        ...prev,
        leads,
        historialEtapas,
        historialEdicionesLead,
        contactosHistorial,
        config: {
          ...prev.config,
          importacionLeads: {
            totalImportados: (prevStats.totalImportados || 0) + nuevos.length,
            totalDuplicados: (prevStats.totalDuplicados || 0) + duplicados.length,
            eventos,
          },
        },
      };
    });
  }, []);

  // ── HISTORIAL DE CONTACTOS ──────────────────────
  const agregarContacto = useCallback((leadId, contacto) => {
    const nuevo = {
      id: uid(),
      leadId,
      fecha: ahora(),
      tipo: "whatsapp", // whatsapp, llamada, presencial, visita, otro
      nota: "",
      ...contacto,
    };
    setState(prev => {
      const lead = prev.leads.find(l => l.id === leadId);
      const oppId = contacto.opportunityId || null;
      let oportunidadesExpansion = prev.oportunidadesExpansion || [];
      if (oppId) {
        oportunidadesExpansion = oportunidadesExpansion.map((o) =>
          o.id === oppId ? { ...o, ultimoContacto: nuevo.fecha, actualizadoEn: ahora() } : o
        );
      }
      const result = {
        ...prev,
        contactosHistorial: [...prev.contactosHistorial, nuevo],
        oportunidadesExpansion,
        leads: prev.leads.map(l =>
          l.id === leadId ? { ...l, ultimoContacto: nuevo.fecha } : l
        ),
      };
      // Auto-create revisit task if diasEntreSeguimiento > 0 and lead is active
      const diasSeg = prev.config.diasEntreSeguimiento;
      if (!oppId && diasSeg > 0 && lead && lead.etapa !== "inscrito" && lead.etapa !== "perdido") {
        const yaTieneTarea = prev.tareasFollowup.some(t => t.leadId === leadId && !t.completada && !t.cancelada);
        if (!yaTieneTarea) {
          const fechaRevisita = new Date(Date.now() + diasSeg * 86400000).toISOString().slice(0, 10);
          result.tareasFollowup = [...result.tareasFollowup, {
            id: uid(), leadId,
            descripcion: `Revisitar a ${lead.nombre}`,
            fechaProgramada: fechaRevisita,
            completada: false, completadaEn: null,
            tipo: "visita", creadaEn: ahora(),
          }];
        }
      }
      return result;
    });
  }, []);

  // ── TAREAS DE FOLLOW-UP ─────────────────────────
  const agregarTarea = useCallback((tarea) => {
    const nueva = {
      id: uid(),
      leadId: null,
      descripcion: "",
      fechaProgramada: hoy(),
      completada: false,
      completadaEn: null,
      tipo: "",  // "", "visita", "llamada", "seguimiento"
      creadaEn: ahora(),
      ...tarea,
    };
    setState(prev => ({
      ...prev,
      tareasFollowup: [...prev.tareasFollowup, nueva],
    }));
  }, []);

  const completarTarea = useCallback((id) => {
    setState(prev => ({
      ...prev,
      tareasFollowup: prev.tareasFollowup.map(t =>
        t.id === id ? { ...t, completada: true, completadaEn: ahora() } : t
      ),
    }));
  }, []);

  const eliminarTarea = useCallback((id) => {
    setState(prev => ({
      ...prev,
      tareasFollowup: prev.tareasFollowup.map(t => t.id === id ? { ...t, cancelada: true, canceladaEn: ahora() } : t),
    }));
  }, []);

  // ── OPORTUNIDADES EXPANSIÓN ─────────────────────
  const agregarOportunidad = useCallback((data) => {
    const ts = ahora();
    const opp = {
      id: uid(),
      leadId: "",
      nombre: "",
      descripcion: "",
      tipo: "nueva_solucion",
      etapa: "interes_detectado",
      montoEstimado: null,
      origen: "",
      detalleOrigen: "",
      vendedorId: null,
      ultimoContacto: null,
      proximoSeguimiento: null,
      proximaAccion: "",
      creadoEn: ts,
      actualizadoEn: ts,
      cerradaEn: null,
      resultado: null,
      historialEtapas: [],
      ...data,
    };
    let createdId = null;
    setState((prev) => {
      if (!prev.leads.some(l => l.id === opp.leadId && !l.archivado)) return prev;
      opp.etapa = "interes_detectado";
      createdId = opp.id;
      const historialEtapas = [
        ...(opp.historialEtapas?.length ? opp.historialEtapas : []),
        { etapaAnterior: null, etapaNueva: opp.etapa, fechaHora: ts, montoEstimado: opp.montoEstimado ?? null },
      ];
      return withPipelineSnapshot({
        ...prev,
        oportunidadesExpansion: [...(prev.oportunidadesExpansion || []), { ...opp, historialEtapas }],
      });
    });
    return createdId;
  }, []);

  const editarOportunidad = useCallback((id, cambios) => {
    const immutable = new Set(["etapa", "historialEtapas", "historialEdiciones", "historialAsignaciones", "creadoEn"]);
    const editable = Object.fromEntries(Object.entries(cambios).filter(([key]) => !immutable.has(key)));
    setState((prev) => withPipelineSnapshot({
      ...prev,
      oportunidadesExpansion: (prev.oportunidadesExpansion || []).map((o) =>
        o.id === id ? { ...o, ...editable, id, actualizadoEn: ahora(), historialEdiciones: [...(o.historialEdiciones || []), { fechaHora: ahora(), cambios: Object.keys(editable).filter(k => JSON.stringify(o[k]) !== JSON.stringify(editable[k])).map(campo => ({ campo, valorAnterior: o[campo] ?? null, valorNuevo: editable[campo] ?? null })) }] } : o
      ),
    }));
  }, []);

  const moverOportunidadEtapa = useCallback((id, nuevaEtapa, detalles = {}) => {
    setState((prev) => {
      const opp = (prev.oportunidadesExpansion || []).find((o) => o.id === id);
      if (!opp || opp.etapa === nuevaEtapa) return prev;
      if (nuevaEtapa === "ganada" && !prev.ventas.some(v => v.opportunityId === id && !v.anulada)) return prev;
      if (nuevaEtapa === "perdida" && !String(detalles.motivoPerdida || opp.motivoPerdida || "").trim()) return prev;
      const ts = ahora();
      const entry = {
        etapaAnterior: opp.etapa,
        etapaNueva: nuevaEtapa,
        fechaHora: ts,
        motivoPerdida: nuevaEtapa === "perdida" ? (detalles.motivoPerdida || opp.motivoPerdida) : null,
        montoEstimado: opp.montoEstimado ?? null,
      };
      const cerrada = ["ganada", "perdida", "pospuesta"].includes(nuevaEtapa);
      const reabrir = isEtapaExpansionAbierta(nuevaEtapa) && ["ganada", "perdida", "pospuesta"].includes(opp.etapa);
      return withPipelineSnapshot({
        ...prev,
        oportunidadesExpansion: prev.oportunidadesExpansion.map((o) =>
          o.id === id
            ? {
                ...o,
                etapa: nuevaEtapa,
                motivoPerdida: nuevaEtapa === "perdida" ? (detalles.motivoPerdida || o.motivoPerdida) : o.motivoPerdida,
                actualizadoEn: ts,
                historialEtapas: [...(o.historialEtapas || []), entry],
                cerradaEn: cerrada ? ts : (reabrir ? null : o.cerradaEn),
                resultado: cerrada ? nuevaEtapa : (reabrir ? null : o.resultado),
              }
            : o
        ),
      });
    });
  }, []);

  const eliminarOportunidad = useCallback((id) => {
    setState((prev) => withPipelineSnapshot({
      ...prev,
      oportunidadesExpansion: (prev.oportunidadesExpansion || []).filter((o) => o.id !== id),
    }));
  }, []);

  // ── VENTAS ──────────────────────────────────────
  const registrarVenta = useCallback((venta) => {
    const nueva = {
      id: uid(),
      fecha: ahora(),
      leadId: null,
      nombreCliente: "",
      productoId: null,
      productoNombre: "",
      items: [],          // [{productoId, productoNombre, cantidad, precioUnitario, subtotal}]
      montoEfectivo: 0,
      montoTransferencia: 0,
      total: 0,
      vendedorId: null,
      vendedorNombre: "",
      esCredito: false,       // venta a crédito
      fechaLimitePago: null,  // YYYY-MM-DD
      anulada: false,
      // Suscripción (solo si producto es recurrente)
      startDate: null,    // YYYY-MM-DD
      endDate: null,      // YYYY-MM-DD
      validityDays: null, // días de vigencia
      reminderDays: null, // días antes para recordatorio
      renovadaDesde: null, // id de venta original si es renovación
      opportunityId: null,
      ...venta,
    };
    // For credit sales: total = sum of items (debt), for regular sales: total = payment
    if (nueva.esCredito) {
      const itemsTotal = totalItems(nueva.items || []);
      nueva.total = itemsTotal > 0 ? itemsTotal : (parseFloat(nueva.montoEfectivo) || 0) + (parseFloat(nueva.montoTransferencia) || 0);
      nueva.montoEfectivo = 0;
      nueva.montoTransferencia = 0;
    } else {
      nueva.total = Math.round(((parseFloat(nueva.montoEfectivo) || 0) + (parseFloat(nueva.montoTransferencia) || 0)) * 100) / 100;
    }

    const nuevoClienteId = nueva.leadId ? null : uid();
    if (nuevoClienteId) nueva.leadId = nuevoClienteId;
    setState(prev => {
      // Direct sales also get a stable customer identity for future purchases.
      let leadsActualizados = nuevoClienteId ? [...prev.leads, {
        id: nuevoClienteId, nombre: nueva.nombreCliente, telefono: "", edad: "", canal: "presencial",
        cursoInteres: nueva.productoNombre, etapa: "inscrito", creadoEn: nueva.fecha,
        fechaContacto: hoy(), ultimoContacto: null, codigoPais: prev.config.codigoPais,
        observaciones: "", vendedorId: nueva.vendedorId,
      }] : prev.leads;
      let nuevoHistorial = prev.historialEtapas;
      if (nuevoClienteId) nuevoHistorial = [...nuevoHistorial, { id: uid(), leadId: nuevoClienteId, etapaAnterior: null, etapaNueva: "inscrito", fechaHora: nueva.fecha }];
      if (nueva.leadId) {
        const leadActual = prev.leads.find(l => l.id === nueva.leadId);
        if (leadActual && leadActual.etapa !== "inscrito") {
          leadsActualizados = prev.leads.map(l =>
            l.id === nueva.leadId ? { ...l, etapa: "inscrito" } : l
          );
          nuevoHistorial = [...prev.historialEtapas, {
            id: uid(),
            leadId: nueva.leadId,
            etapaAnterior: leadActual.etapa,
            etapaNueva: "inscrito",
            fechaHora: ahora(),
          }];
        }
      }
      // Auto-create follow-up task post-sale
      let tareas = prev.tareasFollowup;
      if (nueva.leadId) {
        const diasSeg = prev.config.diasEntreSeguimiento || 7;
        const fechaSeguimiento = new Date(Date.now() + diasSeg * 86400000).toISOString().slice(0, 10);
        tareas = [...tareas, {
          id: uid(), leadId: nueva.leadId,
          descripcion: `Seguimiento post-venta: ${nueva.productoNombre || "producto"}`,
          fechaProgramada: fechaSeguimiento,
          completada: false, completadaEn: null,
          tipo: "seguimiento", creadaEn: ahora(),
        }];
      }
      let oportunidadesExpansion = prev.oportunidadesExpansion || [];
      if (nueva.opportunityId) {
        const opp = oportunidadesExpansion.find((o) => o.id === nueva.opportunityId);
        if (opp && (!nueva.leadId || opp.leadId === nueva.leadId)) {
          if (!["ganada", "perdida", "pospuesta"].includes(opp.etapa)) {
            const tsClose = ahora();
            const entry = {
              etapaAnterior: opp.etapa,
              etapaNueva: "ganada",
              fechaHora: tsClose,
              montoEstimado: nueva.total ?? opp.montoEstimado ?? null,
            };
            oportunidadesExpansion = oportunidadesExpansion.map((o) =>
              o.id === opp.id
                ? {
                    ...o,
                    etapa: "ganada",
                    cerradaEn: tsClose,
                    resultado: "ganada",
                    actualizadoEn: tsClose,
                    historialEtapas: [...(o.historialEtapas || []), entry],
                  }
                : o
            );
          }
        } else {
          nueva.opportunityId = null;
        }
      }
      return withPipelineSnapshot({
        ...prev,
        ventas: [...prev.ventas.map(v => v.id === nueva.recompraDe && !v.leadId ? { ...v, leadId: nueva.leadId } : v), nueva],
        pagosCredito: (prev.pagosCredito || []).map(p => p.ventaId === nueva.recompraDe && prev.ventas.some(v => v.id === p.ventaId && !v.leadId) ? { ...p, clienteKey: nueva.leadId } : p),
        leads: leadsActualizados,
        historialEtapas: nuevoHistorial,
        tareasFollowup: tareas,
        oportunidadesExpansion,
      });
    });
    return nueva.id;
  }, []);

  const anularVenta = useCallback((ventaId) => {
    setState(prev => {
      const sale = prev.ventas.find(v => v.id === ventaId);
      const ventas = prev.ventas.map(v => v.id === ventaId ? { ...v, anulada: true } : v);
      const ts = ahora();
      const oportunidadesExpansion = (prev.oportunidadesExpansion || []).map(o => {
        if (o.id !== sale?.opportunityId || o.etapa !== "ganada" || ventas.some(v => v.opportunityId === o.id && !v.anulada)) return o;
        const stage = [...(o.historialEtapas || [])].reverse().find(h => h.etapaNueva === "ganada")?.etapaAnterior || "interes_detectado";
        return { ...o, etapa: stage, resultado: null, cerradaEn: null, actualizadoEn: ts, historialEtapas: [...(o.historialEtapas || []), { etapaAnterior: "ganada", etapaNueva: stage, fechaHora: ts, motivo: "Venta anulada" }] };
      });
      return withPipelineSnapshot({ ...prev, ventas, oportunidadesExpansion });
    });
  }, []);

  // ── PAGOS A CRÉDITO ─────────────────────────────
  const registrarPagoCredito = useCallback((pago) => {
    const nuevo = {
      id: uid(),
      ventaId: null,
      clienteKey: "",  // leadId || nombreCliente
      monto: 0,
      metodo: "efectivo",
      nota: "",
      fecha: ahora(),
      ...pago,
    };
    setState(prev => {
      // Auto-create cobro follow-up task
      let tareas = prev.tareasFollowup;
      const venta = prev.ventas.find(v => v.id === nuevo.ventaId);
      if (venta) nuevo.clienteKey = venta.leadId || venta.nombreCliente;
      if (venta?.fechaLimitePago) {
        const pagosVenta = [...(prev.pagosCredito || []).filter(p => p.ventaId === nuevo.ventaId), nuevo];
        const totalPagado = pagosVenta.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
        if (totalPagado < venta.total) {
          // Still has remaining debt — create follow-up
          const diasSeg = prev.config.diasEntreSeguimiento || 7;
          const fechaCobro = new Date(Date.now() + diasSeg * 86400000).toISOString().slice(0, 10);
          tareas = [...tareas, {
            id: uid(), leadId: venta.leadId || null,
            descripcion: `Cobrar saldo pendiente: ${venta.nombreCliente}`,
            fechaProgramada: fechaCobro,
            completada: false, completadaEn: null,
            tipo: "cobro", creadaEn: ahora(),
          }];
        }
      }
      return {
        ...prev,
        pagosCredito: [...(prev.pagosCredito || []), nuevo],
        tareasFollowup: tareas,
      };
    });
    return nuevo.id;
  }, []);

  const visibleState = useMemo(() => excluirClientesArchivados(state), [state]);

  // ── MÉTRICAS COMPUTADAS ─────────────────────────
  const metricLeads = useMemo(() => isOwner ? visibleState.leads : visibleState.leads.filter(l => l.vendedorId === accessContext.sellerId), [visibleState.leads, isOwner, accessContext.sellerId]);
  const metricIds = useMemo(() => new Set(metricLeads.map(l => l.id)), [metricLeads]);
  const metricVentas = useMemo(() => isOwner ? visibleState.ventas : visibleState.ventas.filter(v => metricIds.has(v.leadId)), [visibleState.ventas, isOwner, metricIds]);
  const metricOportunidades = useMemo(() => isOwner ? visibleState.oportunidadesExpansion : (visibleState.oportunidadesExpansion || []).filter(o => metricIds.has(o.leadId)), [visibleState.oportunidadesExpansion, isOwner, metricIds]);
  const ventasActivas = useMemo(() => metricVentas.filter(v => !v.anulada), [metricVentas]);

  const ventasDelDia = useMemo(() => {
    const h = hoy();
    return ventasActivas.filter(v => v.fecha.slice(0, 10) === h);
  }, [ventasActivas]);

  const totalVentasDelDia = useMemo(() =>
    ventasDelDia.reduce((s, v) => s + v.total, 0), [ventasDelDia]);

  const ventasDelMes = useMemo(() => {
    const mes = hoy().slice(0, 7);
    return ventasActivas.filter(v => v.fecha.slice(0, 7) === mes);
  }, [ventasActivas]);

  const totalVentasDelMes = useMemo(() =>
    ventasDelMes.reduce((s, v) => s + v.total, 0), [ventasDelMes]);

  // Leads por etapa
  const leadsPorEtapa = useMemo(() => {
    const map = {};
    visibleState.config.etapasPipeline.forEach(e => { map[e] = []; });
    visibleState.leads.forEach(l => {
      if (l.archivado) return;
      if (map[l.etapa]) map[l.etapa].push(l);
      else map[l.etapa] = [l];
    });
    return map;
  }, [visibleState.leads, visibleState.config.etapasPipeline]);

  // Scoring
  const leadsConScore = useMemo(() =>
    visibleState.leads
      .filter(l => l.etapa !== "inscrito" && l.etapa !== "perdido")
      .map(l => ({
        ...l,
        score: calcularScore(l, visibleState.contactosHistorial, visibleState.tareasFollowup),
      }))
      .sort((a, b) => b.score - a.score),
    [visibleState.leads, visibleState.contactosHistorial, visibleState.tareasFollowup]
  );

  // Leads sin seguimiento (>= 3 días sin contacto)
  const leadsSinSeguimiento = useMemo(() => {
    const limite = Date.now() - 3 * 86400000;
    return visibleState.leads
      .filter(l => l.etapa !== "inscrito" && l.etapa !== "perdido")
      .filter(l => {
        if (!l.ultimoContacto) return true;
        return new Date(l.ultimoContacto).getTime() < limite;
      });
  }, [visibleState.leads]);

  // Tareas pendientes hoy
  const tareasHoy = useMemo(() => {
    const h = hoy();
    return visibleState.tareasFollowup.filter(t => !t.completada && !t.cancelada && t.fechaProgramada <= h);
  }, [visibleState.tareasFollowup]);

  // Tareas vencidas
  const tareasVencidas = useMemo(() => {
    const h = hoy();
    return visibleState.tareasFollowup.filter(t => !t.completada && !t.cancelada && t.fechaProgramada < h);
  }, [visibleState.tareasFollowup]);

  // Conversión
  const tasaConversion = useMemo(() => {
    if (metricLeads.length === 0) return 0;
    const buyers = new Set(ventasActivas.map(v => v.leadId));
    const inscritos = metricLeads.filter(l => buyers.has(l.id)).length;
    return Math.round((inscritos / metricLeads.length) * 100);
  }, [metricLeads, ventasActivas]);

  // Ventas por vendedor
  const ventasPorVendedor = useMemo(() => {
    const map = {};
    ventasActivas.forEach(v => {
      const key = v.vendedorNombre || "Sin asignar";
      if (!map[key]) map[key] = { nombre: key, cantidad: 0, total: 0 };
      map[key].cantidad++;
      map[key].total += v.total;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [ventasActivas]);

  // Ventas por canal (a través de leads)
  const ventasPorCanal = useMemo(() => {
    const map = {};
    ventasActivas.forEach(v => {
      const lead = visibleState.leads.find(l => l.id === v.leadId);
      const canal = lead?.canal || "directo";
      if (!map[canal]) map[canal] = { canal, cantidad: 0, total: 0 };
      map[canal].cantidad++;
      map[canal].total += v.total;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [ventasActivas, visibleState.leads]);

  // Ventas por producto
  const ventasPorProducto = useMemo(() => {
    const map = {};
    ventasActivas.forEach(v => {
      const key = v.productoNombre || "Sin producto";
      if (!map[key]) map[key] = { nombre: key, cantidad: 0, total: 0 };
      map[key].cantidad++;
      map[key].total += v.total;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [ventasActivas]);

  // Ingreso por lead
  const ingresoPorLead = useMemo(() => {
    if (metricLeads.length === 0) return 0;
    const totalIngresos = ventasActivas.reduce((s, v) => s + v.total, 0);
    return Math.round(totalIngresos / metricLeads.length);
  }, [metricLeads, ventasActivas]);

  // Clientes frecuentes (2+ compras)
  const clientesFrecuentes = useMemo(() => {
    const map = {};
    ventasActivas.forEach(v => {
      const key = v.leadId || `venta:${v.id}`;
      if (!key) return;
      if (!map[key]) map[key] = { nombre: visibleState.leads.find(l => l.id === v.leadId)?.nombre || v.nombreCliente, leadId: v.leadId, compras: 0, total: 0 };
      map[key].compras++;
      map[key].total += v.total;
    });
    return Object.values(map).filter(c => c.compras >= 2).sort((a, b) => b.compras - a.compras);
  }, [ventasActivas, visibleState.leads]);

  // Leads agrupados por zona
  const leadsPorZona = useMemo(() => {
    const map = {};
    visibleState.leads.forEach(l => {
      const z = l.zona;
      if (!z) return;
      if (!map[z]) map[z] = [];
      map[z].push(l);
    });
    return map;
  }, [visibleState.leads]);

  // Zonas sin cobertura (definidas en config pero sin leads)
  const zonasSinCobertura = useMemo(() => {
    if (!visibleState.config.zonas?.length) return [];
    const zonasConLeads = new Set(visibleState.leads.map(l => l.zona).filter(Boolean));
    return visibleState.config.zonas.filter(z => !zonasConLeads.has(z));
  }, [visibleState.config.zonas, visibleState.leads]);

  // ── CRÉDITO COMPUTADO ──────────────────────────
  const clientesConDeuda = useMemo(() => {
    const map = {};
    ventasActivas.filter(v => v.esCredito).forEach(v => {
      const key = v.leadId || v.nombreCliente;
      if (!key) return;
      if (!map[key]) map[key] = { key, nombre: v.nombreCliente, leadId: v.leadId || null, telefono: null, ventas: [], totalDeuda: 0, totalPagado: 0 };
      // Get telefono from lead if available
      if (v.leadId) {
        const lead = visibleState.leads.find(l => l.id === v.leadId);
        if (lead) { map[key].telefono = lead.telefono; map[key].codigoPais = lead.codigoPais; }
      }
      map[key].ventas.push(v);
      map[key].totalDeuda += v.total;
    });
    // Sum payments
    (visibleState.pagosCredito || []).forEach(p => {
      const venta = ventasActivas.find(v => v.id === p.ventaId && v.esCredito);
      const key = venta && (venta.leadId || venta.nombreCliente);
      if (key && map[key]) map[key].totalPagado += parseFloat(p.monto) || 0;
    });
    return Object.values(map)
      .map(c => ({ ...c, saldoPendiente: Math.max(0, c.totalDeuda - c.totalPagado), fechaLimite: c.ventas.reduce((earliest, v) => (!earliest || (v.fechaLimitePago && v.fechaLimitePago < earliest)) ? v.fechaLimitePago : earliest, null) }))
      .filter(c => c.saldoPendiente > 0)
      .sort((a, b) => (a.fechaLimite || "9").localeCompare(b.fechaLimite || "9"));
  }, [ventasActivas, visibleState.pagosCredito, visibleState.leads]);

  const deudaTotal = useMemo(() => clientesConDeuda.reduce((s, c) => s + c.saldoPendiente, 0), [clientesConDeuda]);

  const cobrosHoy = useMemo(() => {
    const h = hoy();
    return clientesConDeuda.filter(c => c.fechaLimite && c.fechaLimite <= h);
  }, [clientesConDeuda]);

  const pagosDeCliente = useCallback((clienteKey) => {
    return (visibleState.pagosCredito || []).filter(p => p.clienteKey === clienteKey).sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [visibleState.pagosCredito]);

  // Funnel data
  const funnelData = useMemo(
    () => funnelAcumulativo(visibleState.config.etapasPipeline, metricLeads, visibleState.historialEtapas.filter(h => metricIds.has(h.leadId)), ventasActivas),
    [metricLeads, metricIds, visibleState.config.etapasPipeline, visibleState.historialEtapas, ventasActivas]
  );

  // ── FUNNEL TRACKING: métricas por etapa ──────────
  const historicalStages = useMemo(() => buildStageMetrics(
    visibleState.historialEtapas.filter(h => metricIds.has(h.leadId)),
    "0001-01-01", "9999-12-31", visibleState.config
  ), [visibleState.historialEtapas, visibleState.config, metricIds]);
  const tiempoPromedioPorEtapa = useMemo(() => Object.fromEntries(historicalStages.map(s => [s.id, s.diasPromedio])), [historicalStages]);
  const tasaConversionPorEtapa = useMemo(() => Object.fromEntries(historicalStages.map(s => [s.id, s.conversion])), [historicalStages]);

  // Alertas inteligentes
  const alertas = useMemo(() => {
    const arr = [];
    // Leads sin seguimiento
    leadsSinSeguimiento.forEach(l => {
      const dias = l.ultimoContacto
        ? Math.floor((Date.now() - new Date(l.ultimoContacto).getTime()) / 86400000)
        : "nunca contactado";
      arr.push({
        id: `lead-seg:${l.id}`,
        tipo: "warning",
        mensaje: `${l.nombre} lleva ${typeof dias === "number" ? dias + " días" : dias} sin seguimiento`,
        leadId: l.id,
      });
    });
    // Tareas vencidas
    tareasVencidas.forEach(t => {
      const lead = visibleState.leads.find(l => l.id === t.leadId);
      arr.push({
        id: `tarea:${t.id}`,
        tipo: "error",
        mensaje: `Tarea vencida: "${t.descripcion}"${lead ? ` (${lead.nombre})` : ""}`,
        leadId: t.leadId,
      });
    });
    // Top leads por score
    leadsConScore.slice(0, 3).forEach(l => {
      if (l.score >= 40) {
        arr.push({
          id: `score:${l.id}`,
          tipo: "success",
          mensaje: `${l.nombre} tiene prioridad de seguimiento (puntaje: ${l.score})`,
          leadId: l.id,
        });
      }
    });
    // Cobros vencidos
    cobrosHoy.forEach(c => {
      arr.push({
        id: `cobro:${c.key}`,
        tipo: "error",
        mensaje: `💰 Cobro vencido: ${c.nombre} debe ${visibleState.config.moneda} ${c.saldoPendiente.toLocaleString()}`,
      });
    });
    // Cobros próximos (3 días)
    const en3dias = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
    clientesConDeuda.filter(c => c.fechaLimite && c.fechaLimite > hoy() && c.fechaLimite <= en3dias).forEach(c => {
      arr.push({
        id: `cobro-prox:${c.key}:${c.fechaLimite}`,
        tipo: "warning",
        mensaje: `⏰ ${c.nombre} debe pagar antes del ${c.fechaLimite} (${visibleState.config.moneda} ${c.saldoPendiente.toLocaleString()})`,
      });
    });
    return arr;
  }, [leadsSinSeguimiento, tareasVencidas, leadsConScore, visibleState.leads, cobrosHoy, clientesConDeuda, visibleState.config.moneda]);

  // Resumen diario automático
  const generarResumenHoy = useCallback(() => {
    const h = hoy();
    const ventasHoy = ventasActivas.filter(v => v.fecha.slice(0, 10) === h);
    const leadHoy = visibleState.leads.filter(l => l.fechaContacto === h);
    const inscritosHoy = visibleState.leads.filter(l => l.etapa === "inscrito" && l.fechaContacto === h);
    const contactosHoy = visibleState.contactosHistorial.filter(c => c.fecha.slice(0, 10) === h);

    const resumen = {
      id: uid(),
      fecha: h,
      leadsNuevos: leadHoy.length,
      contactosRealizados: contactosHoy.length,
      inscritos: inscritosHoy.length,
      ventasCount: ventasHoy.length,
      totalVentas: ventasHoy.reduce((s, v) => s + v.total, 0),
      totalEfectivo: ventasHoy.reduce((s, v) => s + (parseFloat(v.montoEfectivo) || 0), 0),
      totalTransferencia: ventasHoy.reduce((s, v) => s + (parseFloat(v.montoTransferencia) || 0), 0),
      generadoEn: ahora(),
    };

    setState(prev => {
      // Reemplazar si ya existe uno de hoy
      const sinHoy = prev.resumenDiario.filter(r => r.fecha !== h);
      return { ...prev, resumenDiario: [...sinHoy, resumen] };
    });

    return resumen;
  }, [visibleState.leads, visibleState.contactosHistorial, ventasActivas]);

  const marcarAlertaLeida = useCallback((id) => {
    if (!id) return;
    setState(prev => {
      const actuales = prev.config.alertasLeidas || [];
      if (actuales.includes(id)) return prev;
      return { ...prev, config: { ...prev.config, alertasLeidas: [...actuales, id] } };
    });
  }, []);

  const marcarAlertasLeidas = useCallback((ids = []) => {
    const nuevos = ids.filter(Boolean);
    if (!nuevos.length) return;
    setState(prev => {
      const actuales = prev.config.alertasLeidas || [];
      const merged = [...actuales];
      nuevos.forEach((id) => { if (!merged.includes(id)) merged.push(id); });
      if (merged.length === actuales.length) return prev;
      return { ...prev, config: { ...prev.config, alertasLeidas: merged } };
    });
  }, []);

  const pruneAlertasLeidas = useCallback((idsVigentes = []) => {
    const vigentes = new Set(idsVigentes.filter(Boolean));
    setState(prev => {
      const actuales = prev.config.alertasLeidas || [];
      const next = actuales.filter(id => vigentes.has(id));
      if (next.length === actuales.length) return prev;
      return { ...prev, config: { ...prev.config, alertasLeidas: next } };
    });
  }, []);

  const resumenDiarioComputado = useMemo(() => {
    const fechas = new Set();
    for (let i = 0; i < 30; i += 1) {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() - i);
      fechas.add(toLocalISOString(d).slice(0, 10));
    }
    ventasActivas.forEach((v) => fechas.add(v.fecha.slice(0, 10)));
    visibleState.leads.forEach((l) => {
      if (l.creadoEn) fechas.add(l.creadoEn.slice(0, 10));
    });
    visibleState.contactosHistorial.forEach((c) => fechas.add(c.fecha.slice(0, 10)));
    visibleState.historialEtapas.forEach((h) => fechas.add(h.fechaHora.slice(0, 10)));

    const filas = [...fechas].sort((a, b) => b.localeCompare(a)).map((fecha) => {
      const ventasDia = ventasActivas.filter((v) => v.fecha.slice(0, 10) === fecha);
      return {
        id: fecha,
        fecha,
        leadsNuevos: metricLeads.filter((l) => (l.creadoEn || "").slice(0, 10) === fecha).length,
        contactosRealizados: visibleState.contactosHistorial.filter((c) => metricIds.has(c.leadId) && c.fecha.slice(0, 10) === fecha).length,
        inscritos: visibleState.historialEtapas.filter(
          (h) => metricIds.has(h.leadId) && h.etapaNueva === ETAPA_VENTA_CERRADA && h.fechaHora.slice(0, 10) === fecha
        ).length,
        ventasCount: ventasDia.length,
        totalVentas: Math.round(ventasDia.reduce((s, v) => s + (Number(v.total) || 0), 0) * 100) / 100,
        totalEfectivo: ventasDia.reduce((s, v) => s + (parseFloat(v.montoEfectivo) || 0), 0),
        totalTransferencia: ventasDia.reduce((s, v) => s + (parseFloat(v.montoTransferencia) || 0), 0),
      };
    });

    return filas
      .filter((r) => r.leadsNuevos || r.contactosRealizados || r.inscritos || r.ventasCount)
      .slice(0, 14);
  }, [visibleState.leads, metricLeads, metricIds, visibleState.contactosHistorial, visibleState.historialEtapas, ventasActivas]);

  const resumenComercial = useMemo(
    () => resumenComercialGlobal({
      leads: metricLeads,
      ventas: metricVentas,
      oportunidades: metricOportunidades || [],
      ventasActivas,
      diasEstancamiento: visibleState.config.diasEntreSeguimiento || 7,
    }),
    [metricLeads, metricVentas, metricOportunidades, ventasActivas, visibleState.config.diasEntreSeguimiento]
  );

  return {
    isOwner,
    metricLeads,
    metricVentas,
    metricOportunidades,
    ready,
    accessError,
    workspace,
    sellerId: accessContext.sellerId,
    sellerLimit: accessContext.sellerLimit || 3,
    // State
    config: visibleState.config,
    leads: visibleState.leads.filter(l => !l.archivado),
    leadsArchivados: isOwner ? state.leads.filter(l => l.archivado) : [],
    historialArchivado: isOwner ? { contactosHistorial: state.contactosHistorial, tareasFollowup: state.tareasFollowup, historialEtapas: state.historialEtapas, historialEdicionesLead: state.historialEdicionesLead, historialAsignaciones: state.historialAsignaciones, ventas: state.ventas, oportunidadesExpansion: state.oportunidadesExpansion } : null,
    contactosHistorial: visibleState.contactosHistorial,
    tareasFollowup: visibleState.tareasFollowup,
    ventas: visibleState.ventas,
    pagosCredito: visibleState.pagosCredito || [],
    resumenDiario: resumenDiarioComputado,
    historialEtapas: visibleState.historialEtapas,
    historialEdicionesLead: visibleState.historialEdicionesLead || [],
    historialAsignaciones: visibleState.historialAsignaciones || [],
    oportunidadesExpansion: visibleState.oportunidadesExpansion || [],
    snapshotsPipeline: visibleState.snapshotsPipeline || [],

    // Actions
    actualizarConfig,
    agregarCanalesSiFaltan,
    guardarEtapas,
    agregarProducto,
    agregarProductosMasivo,
    editarProducto,
    agregarVendedor,
    toggleVendedor,
    agregarPlantilla,
    editarPlantilla,
    eliminarPlantilla,
    agregarLead,
    importarLeadsMasivo,
    editarLead,
    archivarLead,
    eliminarLead,
    moverLeadEtapa,
    agregarContacto,
    agregarTarea,
    completarTarea,
    eliminarTarea,
    agregarOportunidad,
    editarOportunidad,
    moverOportunidadEtapa,
    eliminarOportunidad,
    registrarVenta,
    anularVenta,
    registrarPagoCredito,
    generarResumenHoy,
    marcarAlertaLeida,
    marcarAlertasLeidas,
    pruneAlertasLeidas,

    // Computed
    ventasDelDia,
    totalVentasDelDia,
    ventasDelMes,
    totalVentasDelMes,
    leadsPorEtapa,
    leadsConScore,
    leadsSinSeguimiento,
    tareasHoy,
    tareasVencidas,
    tasaConversion,
    ventasPorVendedor,
    ventasPorCanal,
    ventasPorProducto,
    ingresoPorLead,
    funnelData,
    alertas,
    ventasActivas,
    tiempoPromedioPorEtapa,
    tasaConversionPorEtapa,
    clientesFrecuentes,
    leadsPorZona,
    zonasSinCobertura,
    clientesConDeuda,
    deudaTotal,
    cobrosHoy,
    pagosDeCliente,
    resumenComercial,

    // Sync
    connectionStatus, syncConflict, exportPendingChanges, useServerVersion,
    syncStatus,
    ultimoSync,
    cambiosPendientes,
    forzarSync,
    pullSeguro,
    restaurarDesdeBackup,
  };
}
